import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Package, MapPin, User } from 'lucide-react';

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
    fetchAddresses();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await api.get('/addresses');
      setAddresses(response.data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600';
      case 'pending': return 'text-yellow-600';
      case 'shipped': return 'text-blue-600';
      case 'delivered': return 'text-green-700';
      case 'failed': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8">
      <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        My Account
      </h1>

      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="mb-8">
          <TabsTrigger value="orders" data-testid="orders-tab">
            <Package className="mr-2 h-4 w-4" />
            Orders
          </TabsTrigger>
          <TabsTrigger value="addresses" data-testid="addresses-tab">
            <MapPin className="mr-2 h-4 w-4" />
            Addresses
          </TabsTrigger>
          <TabsTrigger value="profile" data-testid="profile-tab">
            <User className="mr-2 h-4 w-4" />
            Profile
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="space-y-6" data-testid="orders-list">
          {orders.length === 0 ? (
            <div className="text-center py-12" data-testid="no-orders-message">
              <p className="text-lg text-muted-foreground mb-4">You haven't placed any orders yet.</p>
              <Link to="/products">
                <Button className="rounded-none px-8 py-4 uppercase tracking-widest text-xs">Start Shopping</Button>
              </Link>
            </div>
          ) : (
            orders.map((order) => (
              <div key={order.id} className="border border-border p-6" data-testid={`order-${order.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Order ID: {order.id}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString('en-IN', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                  <span className={`text-sm uppercase tracking-widest font-bold ${getStatusColor(order.status)}`} data-testid={`order-status-${order.id}`}>
                    {order.status}
                  </span>
                </div>
                <div className="space-y-3 mb-4">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <img src={item.product_image} alt={item.product_name} className="w-16 h-16 object-cover" />
                      <div className="flex-1">
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-light">₹{(item.price * item.quantity).toLocaleString('en-IN')}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-border pt-4 flex justify-between">
                  <span className="text-lg font-medium">Total</span>
                  <span className="text-lg font-light">₹{order.total_amount.toLocaleString('en-IN')}</span>
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="addresses" className="space-y-6" data-testid="addresses-list">
          {addresses.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-muted-foreground">No saved addresses.</p>
            </div>
          ) : (
            addresses.map((addr) => (
              <div key={addr.id} className="border border-border p-6">
                <p className="font-medium mb-2">{addr.name}</p>
                <p className="text-sm text-muted-foreground">{addr.phone}</p>
                <p className="text-sm text-muted-foreground">
                  {addr.address_line1}, {addr.address_line2 && `${addr.address_line2}, `}
                  {addr.city}, {addr.state} - {addr.pincode}
                </p>
                {addr.is_default && (
                  <span className="inline-block mt-2 text-xs uppercase tracking-widest font-bold text-primary">Default</span>
                )}
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="profile" data-testid="profile-info">
          <div className="border border-border p-8">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Profile Information</h3>
            <div className="space-y-4 mb-8">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Name</p>
                <p className="text-lg">{user?.name}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Email</p>
                <p className="text-lg">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="rounded-none"
              data-testid="profile-logout-button"
            >
              Logout
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Profile;